import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Plan, PlanFormData } from '@/types/plan';

export const usePlans = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const fetchPlans = async () => {
    try {
      const storedPlans = localStorage.getItem('plans');
      setPlans(storedPlans ? JSON.parse(storedPlans) : []);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível carregar os planos.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addPlan = async (plan: PlanFormData) => {
    try {
      const newPlan: Plan = {
        ...plan,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const updatedPlans = [newPlan, ...plans];
      setPlans(updatedPlans);
      localStorage.setItem('plans', JSON.stringify(updatedPlans));
      
      toast({
        title: "Sucesso",
        description: "Plano cadastrado com sucesso!",
      });

      return newPlan;
    } catch (error) {
      console.error('Error adding plan:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível cadastrar o plano.",
      });
      throw error;
    }
  };

  const updatePlan = async (id: string, updates: PlanFormData) => {
    try {
      const updatedPlan: Plan = {
        ...updates,
        id,
        updated_at: new Date().toISOString()
      };

      const updatedPlans = plans.map(plan => 
        plan.id === id ? { ...plan, ...updatedPlan } : plan
      );

      setPlans(updatedPlans);
      localStorage.setItem('plans', JSON.stringify(updatedPlans));

      toast({
        title: "Sucesso",
        description: "Plano atualizado com sucesso!",
      });

      return updatedPlan;
    } catch (error) {
      console.error('Error updating plan:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o plano.",
      });
      throw error;
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  return {
    plans,
    isLoading,
    addPlan,
    updatePlan,
    refreshPlans: fetchPlans,
  };
};